<template>
  <div v-if="isAuthenticated">
    <div class="profile-dropdown">
      <button class="profile-button" @click="toggleDropdown" aria-label="Profile menu">
        <i class="fa fa-user-circle"></i>
      </button>
      <div v-if="isOpen" class="profile-dropdown-menu" @click.stop>
        <div class="profile-dropdown-header">{{ displayName }}</div>
        <div class="profile-dropdown-divider"></div>
        <router-link v-if="!disableNavigation && !disablePortalLink" :to="portalRoute" class="profile-dropdown-item" @click.native="closeDropdown">
          <i class="fa fa-th-large"></i> {{ portalLabel }}
        </router-link>
        <span v-else class="profile-dropdown-item profile-dropdown-item-disabled">
          <i class="fa fa-th-large"></i> {{ portalLabel }}
        </span>
        <button class="profile-dropdown-item" @click="openSupportModal">
          <i class="fa fa-envelope"></i> Contact Support
        </button>
        <div class="profile-dropdown-divider"></div>
        <button v-if="!disableNavigation" class="profile-dropdown-item profile-logout-item" @click="handleLogout">
          <i class="fa fa-sign-out"></i> Log Out
        </button>
        <span v-else class="profile-dropdown-item profile-dropdown-item-disabled profile-logout-item">
          <i class="fa fa-sign-out"></i> Log Out
        </span>
      </div>
    </div>

    <!-- Support Modal (outside profile-dropdown to avoid stacking context issues) -->
    <div v-if="showSupportModal" class="viva-modal-backdrop" @click="showSupportModal = false">
      <div class="viva-modal-content support-modal" @click.stop>
        <div class="viva-modal-header">
          <h2>Contact Support</h2>
          <button class="support-modal-close-btn" @click="showSupportModal = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="viva-modal-body">
          <p class="support-modal-text">
            If you have any questions, concerns, or are experiencing technical issues with your VIVA,
            our support team is here to help.
          </p>
          <div class="support-modal-email">
            <i class="fa fa-envelope support-modal-email-icon"></i>
            <a href="mailto:viva@initialview.com" target="_blank" rel="noopener noreferrer" class="support-modal-email-link">viva@initialview.com</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js'

export default {
  name: 'ProfileDropdown',
  props: {
    studentName: {
      type: String,
      default: ''
    },
    isAssignment: {
      type: Boolean,
      default: false
    },
    currentPortal: {
      type: String,
      default: null,
      validator: (value) => [null, 'admissions', 'assignment', 'admin', 'instructor'].includes(value)
    },
    disableNavigation: {
      type: Boolean,
      default: false
    },
    disablePortalLink: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      showSupportModal: false
    }
  },
  computed: {
    isAuthenticated() {
      // Check sessionStorage OR if we received a studentName from API (proves user is authenticated)
      const hasToken = !!(sessionStorage.getItem('apitoken') || sessionStorage.getItem('vparam'))
      const hasStudentName = !!(this.studentName && this.studentName.trim())
      return hasToken || hasStudentName
    },
    displayName() {
      const name = this.studentName ? this.studentName.trim() : ''
      const nameWithoutPunctuation = name.replace(/,/g, '').trim()
      if (nameWithoutPunctuation) {
        return name
      }
      return 'Welcome!'
    },
    portalLabel() {
      // Determine what portal to link to based on context
      if (this.currentPortal === 'admin') {
        return 'Instructor Dashboard'
      } else if (this.currentPortal === 'instructor') {
        return 'Admin Dashboard'
      } else if (this.currentPortal === 'admissions') {
        return 'Assignment Portal'
      } else if (this.currentPortal === 'assignment') {
        return 'Admissions Portal'
      } else if (this.isAssignment) {
        return 'Assignment Portal'
      } else {
        return 'Admissions Portal'
      }
    },
    portalRoute() {
      // Determine route based on portal label
      if (this.portalLabel === 'Admin Dashboard') {
        return '/admin/dashboard'
      } else if (this.portalLabel === 'Instructor Dashboard') {
        return '/instructor/dashboard'
      } else if (this.portalLabel === 'Assignment Portal') {
        return '/student/dashboard'
      } else {
        return '/student/portal'
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
    },
    closeDropdown() {
      this.isOpen = false
    },
    openSupportModal() {
      this.isOpen = false
      this.showSupportModal = true
    },
    handleClickOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.isOpen = false
      }
    },
    handleLogout() {
      // Clear all session storage
      sessionStorage.removeItem('apitoken')
      sessionStorage.removeItem('vparam')
      sessionStorage.removeItem('upload_token')

      // Clear Authorization header
      delete apiClient.defaults.headers['Authorization']

      // Navigate to login
      this.$router.push('/login')
    }
  }
}
</script>

<style>
@import '../styles/viva-classes.css';

.profile-dropdown {
  position: relative;
  margin-right: 15px;
  display: flex;
  align-items: center;
}

.profile-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  font-size: 28px;
  color: var(--viva-green);
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  position: relative;
  z-index: 10;
}

.profile-button:hover {
  color: var(--viva-orange);
}

.profile-dropdown-menu {
  position: absolute;
  display: block;
  top: 100%;
  right: 0;
  left: auto;
  background: #ffffff;
  border: 2px solid var(--viva-green, #80C297);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 9999;
  overflow: visible;
  margin-top: 5px;
}

.profile-dropdown-header {
  padding: 12px 16px;
  font-weight: 600;
  color: var(--viva-dark);
  background: var(--viva-cream);
  font-size: 14px;
  border-radius: 6px 6px 0 0;
}

.profile-dropdown-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 0;
}

.profile-dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  color: var(--viva-dark);
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s ease;
  cursor: pointer;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
}

.profile-dropdown-item:hover {
  background: var(--viva-cream);
  text-decoration: none;
  color: var(--viva-dark);
}

.profile-dropdown-item i {
  margin-right: 10px;
  width: 16px;
  text-align: center;
  color: var(--viva-green);
}

.profile-dropdown-item-disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.profile-logout-item {
  color: #dc3545;
}

.profile-logout-item:hover {
  background: #fff5f5;
}

.profile-logout-item i {
  color: #dc3545;
}

/* Support Modal */
.support-modal {
  max-width: 450px;
}

.support-modal-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.support-modal-text {
  color: #333;
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 20px 0;
}

.support-modal-email {
  background: #f5f5f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  text-align: center;
}

.support-modal-email-icon {
  color: var(--viva-green);
  font-size: 18px;
  margin-right: 12px;
}

.support-modal-email-link {
  color: var(--viva-orange);
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
}
</style>
